/**
 * LinkingColumn - Single column of entity cards with connection dots
 *
 * Features:
 * - Animated reordering with framer-motion
 * - Connection dots for drag-to-link
 * - Visual feedback for drag state
 */

import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';

import { cn } from '@/lib/utils';
import type { FinchEntity } from '@/services/api/entitySyncService';
import type {
  DetailedFilevineCollectionItemSchema,
  FilevineEntitySyncSuggestionSetSchema,
} from '@/services/api/generated/entitySyncService';

import { FilevineEntityCard } from '../FilevineEntityCard';
import { type CanonicalSectionType, FinchEntityCard } from '../finch-cards';
import { EntitySyncBadge } from '../sync-suggestions/EntitySyncBadge';
import type { SyncSuggestionsByEntity } from '../sync-suggestions/useSyncSuggestionsMap';
import type {
  DragState,
  FilevineSectionConfigDataSchema,
  FilevineSectionFieldConfigSchema,
  Point,
  ResolvedLink,
  SortedEntity,
} from '../types/linking-view.types';
import { EntityCardWrapper } from './EntityCardWrapper';

interface LinkingColumnProps {
  title: string;
  icon: React.ReactNode;
  entities: SortedEntity<FinchEntity | DetailedFilevineCollectionItemSchema>[];
  columnType: 'finch' | 'filevine';
  canonicalSectionType: CanonicalSectionType;
  fieldConfigs: FilevineSectionFieldConfigSchema[];
  sectionConfigData?: FilevineSectionConfigDataSchema;
  matterId: string;
  onConnectionDotRef: (
    entityId: string,
    element: HTMLDivElement | null,
  ) => void;
  dragState: DragState;
  onDragStart: (
    entityId: string,
    entityType: 'finch' | 'filevine',
    position: Point,
  ) => void;
  onDragEnd: () => void;
  getLinkForEntity: (entityId: string) => ResolvedLink | undefined;
  setValidDropTarget: (entityId: string | null) => void;
  /** Sync suggestions map for showing badges on unlinked entities */
  syncSuggestionsMap?: SyncSuggestionsByEntity;
  /** Callback when sync suggestion badge is clicked */
  onSyncSuggestionClick?: (
    suggestions: FilevineEntitySyncSuggestionSetSchema[],
  ) => void;
}

/**
 * Get entity ID regardless of type
 */
function getEntityId(
  entity: FinchEntity | DetailedFilevineCollectionItemSchema,
): string {
  return entity.id;
}

/**
 * Type guard for Finch entity (not a Filevine collection item)
 */
function isFinchEntity(
  entity: FinchEntity | DetailedFilevineCollectionItemSchema,
): entity is FinchEntity {
  return !('latest_snapshot' in entity);
}

export const LinkingColumn: React.FC<LinkingColumnProps> = ({
  title,
  icon,
  entities,
  columnType,
  canonicalSectionType,
  fieldConfigs,
  sectionConfigData,
  matterId,
  onConnectionDotRef,
  dragState,
  onDragStart,
  onDragEnd,
  getLinkForEntity,
  setValidDropTarget,
  syncSuggestionsMap,
  onSyncSuggestionClick,
}) => {
  const side = columnType === 'finch' ? 'right' : 'left';

  // Determine if an entity is a valid drop target during drag
  const isValidDropTarget = (entityId: string): boolean => {
    if (!dragState.isDragging || !dragState.source) return false;

    // Can only drop on opposite column
    if (dragState.source.entityType === columnType) return false;

    // Check if entity is already linked
    const link = getLinkForEntity(entityId);
    if (link && link.connectionStatus === 'approved') return false;

    return true;
  };

  // Handle mouse enter on potential drop target
  const handleMouseEnter = (entityId: string) => {
    if (isValidDropTarget(entityId)) {
      setValidDropTarget(entityId);
    }
  };

  // Handle mouse leave
  const handleMouseLeave = () => {
    setValidDropTarget(null);
  };

  return (
    <div className="flex flex-col">
      {/* Column header */}
      <div className="mb-3 flex items-center gap-2">
        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-zinc-100 text-zinc-600">
          {icon}
        </div>
        <h4 className="font-medium text-zinc-900">{title}</h4>
        <span className="text-sm text-zinc-500">({entities.length})</span>
      </div>

      {/* Entity list */}
      <div className="space-y-2">
        <AnimatePresence mode="popLayout">
          {entities.map((sortedEntity) => {
            const { entity } = sortedEntity;
            const entityId = getEntityId(entity);
            const link = getLinkForEntity(entityId);
            const isDragSource =
              dragState.isDragging && dragState.source?.entityId === entityId;
            const canBeDropTarget = isValidDropTarget(entityId);
            const isCurrentDropTarget = dragState.validDropTarget === entityId;
            // Disable dragging for entities that already have an approved link
            const hasApprovedLink = link?.connectionStatus === 'approved';

            // Get sync suggestions for this entity (only for unlinked entities)
            const entitySyncSuggestions: FilevineEntitySyncSuggestionSetSchema[] =
              [];
            if (!sortedEntity.isLinked && syncSuggestionsMap) {
              // For Finch entities, look up by Finch ID
              // For Filevine entities, look up by Filevine ID
              const suggestionsMap =
                columnType === 'finch'
                  ? syncSuggestionsMap.byFinchId.get(entityId)
                  : syncSuggestionsMap.byFilevineId.get(entityId);
              if (suggestionsMap) {
                entitySyncSuggestions.push(...suggestionsMap);
              }
            }

            return (
              <motion.div
                key={entityId}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{
                  layout: { type: 'spring', stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 },
                }}
                onMouseEnter={() => handleMouseEnter(entityId)}
                onMouseLeave={handleMouseLeave}
              >
                <EntityCardWrapper
                  entityId={entityId}
                  isLinked={sortedEntity.isLinked}
                  linkStatus={link?.connectionStatus ?? null}
                  side={side}
                  onConnectionDotRef={(el) => onConnectionDotRef(entityId, el)}
                  isDragSource={isDragSource}
                  isValidDropTarget={canBeDropTarget}
                  isCurrentDropTarget={isCurrentDropTarget}
                  isDragDisabled={hasApprovedLink}
                  onDragStart={(position) =>
                    onDragStart(entityId, columnType, position)
                  }
                  onDragEnd={onDragEnd}
                  syncBadge={
                    entitySyncSuggestions.length > 0 &&
                    onSyncSuggestionClick ? (
                      <EntitySyncBadge
                        suggestionSets={entitySyncSuggestions}
                        entityType={columnType}
                        onClick={() =>
                          onSyncSuggestionClick(entitySyncSuggestions)
                        }
                      />
                    ) : undefined
                  }
                >
                  {columnType === 'finch' && isFinchEntity(entity) ? (
                    <FinchEntityCard
                      entity={entity}
                      canonicalSectionType={canonicalSectionType}
                      matterId={matterId}
                      compact
                    />
                  ) : (
                    <FilevineEntityCard
                      entity={entity as DetailedFilevineCollectionItemSchema}
                      fieldConfigs={fieldConfigs}
                      sectionConfigData={sectionConfigData}
                      compact
                    />
                  )}
                </EntityCardWrapper>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {/* Empty state */}
        {entities.length === 0 && (
          <div
            className={cn(
              'flex items-center justify-center rounded-lg border-2 border-dashed border-zinc-200 py-8',
              'text-sm text-zinc-400',
            )}
          >
            No {columnType === 'finch' ? title.toLowerCase() : 'Filevine items'}
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkingColumn;
